import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Building2, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CircleSlash, Download, FileSearch, FileSpreadsheet, FileText, FlaskConical,
  KeyRound, LayoutDashboard, Lock, LockKeyhole, LogOut, Mail, MapPin, MessageSquareText, Microscope, Package, Paperclip, Phone, Plus, Receipt, Repeat, Upload,
  UserRound, X,
} from 'lucide-react';
import {
  addSubmissionFiles, answerQc, askPasswordReset, askPatientReports, checkSamples, choosePassword, customerSession, declinePatientReports, downloadTemplate,
  fileForUpload, joinWithLink, loadMe, loadOverview, loadSubmission, openDocument, openPatientReport, openSubmissionFile, readLink, readPasswordReset,
  removeSubmissionFile, sendSamples, signIn, signOut,
} from './customerApi';
import CustomerSharedData from './CustomerSharedData';
import { tx } from './i18n';
import { Empty, Failed, Loading, Pill, WorkspaceTabs, XomicsSelect } from './parts';
import QcTable from './QcTable';
import { FILE_ACCEPT, MAX_FILE_BYTES, fileSize } from './qcResults';
import SampleAnswersTable from './SampleAnswersTable';
import { errorMessage } from './ui';
import logo from './logo.png';
import './organizations.css';
import './customerPortal.css';

// A customer's own portal (/xomics/customer-portal), their account with us, open at any time, in
// the portal's own frame: a sidebar (Overview, Projects, Invoices, their account), a top bar, pages
// like ours. Everything is a project: Phenome asks them for samples of one workflow at a price and
// says which of our laboratories they go to. A one-time project is for a number of samples, sent
// once (Waiting for your samples, then where they are). A continuous one stays open for as long as
// we work together: they send samples whenever they have them. Each box they send is a submission,
// filled in with the submission form in four steps: what they send, their sample form (the template
// and their filled one, read back), where the box goes, and submitting it; the laboratory then
// works its samples (In the laboratory) until all are done (Done). At the very end of a submission
// they may ask for a patient report on each person, giving each one's full name, sex and date of
// birth; each report is made from that sample's analysis and downloaded here. A project's price and
// invoices are on it: pro forma invoices (a preview of the bill, nothing to pay) and the invoices
// they pay by a due date. The overview shows what needs them (projects to fill in, patient reports
// to answer, the pro forma of a project that waits for them to look over, invoices to pay), their
// projects and their contact at Phenome. For another workflow they ask their contact, who makes a
// new project. Their people come in through the account link we email them, and choose a new
// password through a link of their own when they forget it.
const t = (key, values) => tx(`ui.customer_portal.${key}`, values);
const counted = (key, count) => t(count === 1 ? `${key}_one` : key, { count });
const BASE = '/xomics/customer-portal';
const STAGES = ['requested', 'sent', 'in_lab', 'done'];
// A submission's own journey: the box submitted, its samples in the laboratory, all done.
const BOX_STAGES = ['sent', 'in_lab', 'done'];
const INVOICE_TONES = { due: 'warn', overdue: 'bad', paid: 'ok', proforma: 'info' };
const SAMPLE_TONES = { waiting: 'flat', received: 'info', in_progress: 'info', completed: 'ok', stopped: 'bad' };
const REPORT_TONES = { making: 'info', review: 'info', ready: 'ok', held: 'warn', failed: 'bad' };
// An invoice they still have to pay: due, or overdue. A pro forma never is.
const toPay = (invoice) => invoice.status === 'due' || invoice.status === 'overdue';
const projectPath = (uuid) => `${BASE}/projects/${uuid}`;
const boxPath = (uuid, number) => `${projectPath(uuid)}/submissions/${number}`;
// Where a submission is seen: a one-time project shows its one submission on its own page.
const boxLink = (project, box) => (project.kind === 'continuous' ? boxPath(project.uuid, box.number) : projectPath(project.uuid));
// The submissions waiting for their answer on patient reports: at their very end, not answered yet.
const reportsToAnswer = (projects) => projects.flatMap((project) => project.submissions
  .filter((box) => box.reportsOffered && box.reports === null).map((box) => ({ project, box })));
// The submissions whose QC results wait for their answer: go on, or stop.
const qcToAnswer = (projects) => projects.flatMap((project) => project.submissions
  .filter((box) => box.qcWaiting > 0).map((box) => ({ project, box })));

const day =(value) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
const shortDay = (value) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(value));
function amount(minor, currency, minorUnit) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: minorUnit, maximumFractionDigits: minorUnit })
      .format(Number(minor) / 10 ** minorUnit);
  } catch (_error) {
    return `${(Number(minor) / 10 ** minorUnit).toFixed(minorUnit)} ${currency}`;
  }
}
const initialsOf = (first, last) => `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
const countryOf = (code) => {
  try { return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) || code; } catch (_error) { return code; }
};

// Where to go once signed in: the page of this portal the address asked for (a submission, from its
// email), or its overview.
function nextOf(params) {
  const next = params.get('next') || '';
  const allowed = next.startsWith(`${BASE}/`) && !next.includes('//') && !/^\/xomics\/customer-portal\/(sign-in|join|forgot|reset)/.test(next);
  return allowed ? next : BASE;
}

function SampleStatus({ sample }) {
  // Waiting for their answer after our first quality check; or stopped because they said so.
  if (sample.qc?.waiting) return <span className="xo-cp-status"><Pill tone="warn">{t('status.waiting_for_you')}</Pill></span>;
  if (sample.status === 'stopped' && sample.qc?.result === 'stop') return <span className="xo-cp-status"><Pill tone="bad">{t('status.stopped_by_you')}</Pill></span>;
  return <span className="xo-cp-status">
    <Pill tone={SAMPLE_TONES[sample.status] || 'flat'}>{t(`status.${sample.status}`)}</Pill>
    {/* The step it is at: the one after those done. */}
    {sample.status === 'in_progress' && sample.step && <small>{t('step_of', { done: sample.completedSteps + 1, total: sample.totalSteps, step: sample.step })}</small>}
  </span>;
}

// Where a project is, as they read it: a continuous one open (or closed by Phenome); a one-time one
// waiting for their samples, or where its one submission is.
function stateOf(project) {
  if (project.kind === 'continuous') return project.stage === 'open' ? ['ok', 'state.open'] : ['flat', 'state.closed'];
  if (project.stage === 'requested') return ['warn', 'state.requested'];
  return boxState(project);
}

// Where a submission is: submitted (the box not here yet); arriving, all received, or in the
// laboratory; or done.
function boxState({ stage, progress, sentCount, receivedCount }) {
  if (stage === 'done') return ['ok', 'state.done'];
  if (stage === 'sent') return ['flat', 'state_submitted'];
  if (progress.in_progress + progress.completed + progress.stopped > 0) return ['info', 'state.in_lab'];
  return receivedCount < sentCount ? ['info', 'state.arriving'] : ['ok', 'state.all_received'];
}

// The samples of a submission as one bar: done, in the laboratory, received; the rest not yet arrived.
function ProgressBar({ progress, total }) {
  return <span className="xo-cust-bar" aria-hidden="true">
    {['completed', 'in_progress', 'received'].map((status) => progress[status] > 0
      && <i key={status} className={`is-${status}`} style={{ width: `${(progress[status] / Math.max(total, 1)) * 100}%` }} />)}
  </span>;
}

// Requested, Submitted, In the laboratory, Done, as the portal draws a sample's journey: a check for
// each step passed, a dot for where it is now, with its date where there is one.
function Steps({ stages, stage, when }) {
  const reached = stages.indexOf(stage);
  const done = stage === 'done';
  return <div className="xo-sample-mini-journey xo-cust-journey" aria-label={t('stage_label')}>
    {stages.map((step, index) => {
      const state = done || index < reached ? 'is-previous' : index === reached ? 'is-current' : 'is-next';
      return <span key={step} className={state}>
        <i>{state === 'is-previous' && <Check aria-hidden="true" />}</i>
        <span><strong>{t(`stage.${step}`)}</strong><small>{index <= reached || done ? when[step] : ''}</small></span>
      </span>;
    })}
  </div>;
}

// A one-time project's journey, from asked for to done.
function Journey({ submission }) {
  return <Steps stages={STAGES} stage={submission.stage} when={{ requested: day(submission.requestedAt), sent: submission.sentAt ? day(submission.sentAt) : '',
    in_lab: submission.sentAt ? t('received_of', { received: submission.receivedCount, count: submission.sentCount }) : '', done: '' }} />;
}

// A submission's journey, from its box submitted to done.
function BoxJourney({ box }) {
  return <Steps stages={BOX_STAGES} stage={box.stage} when={{ sent: day(box.sentAt),
    in_lab: t('received_of', { received: box.receivedCount, count: box.sentCount }), done: '' }} />;
}

function invoiceWhen(invoice) {
  if (invoice.status === 'proforma') return t('no_due_date');
  return invoice.status === 'paid' ? t('paid_on', { date: day(invoice.paidAt) }) : t('due_on', { date: day(invoice.dueAt) });
}

// What an invoice bills, in a few words: its samples and its other costs.
function billsLine(invoice) {
  const costs = invoice.lines.filter((line) => !line.billsSamples).length;
  return [invoice.samples > 0 ? counted('samples_count', invoice.samples) : '', costs > 0 ? counted('costs_count', costs) : '']
    .filter(Boolean).join(' · ');
}

// ------------------------------------------------------------------ signed out

function Frame({ children }) {
  return <main id="xo" className="xo-cp-root">
    <div className="xo-auth xo-cp-auth">
      <div className="xo-auth-shell">
        <aside className="xo-auth-brand" aria-hidden="true">
          <div className="xo-auth-hero"><h1>{t('brand_title')}</h1><p>{t('brand_detail_submissions')}</p></div>
        </aside>
        <section className="xo-auth-card"><div className="xo-auth-card-inner">
          <div className="xo-auth-form-logo"><img src={logo} alt="" /></div>
          {children}
        </div></section>
      </div>
    </div>
  </main>;
}

function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(''), [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  if (customerSession()) return <Navigate to={nextOf(params)} replace />;
  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      await signIn(email, password);
      navigate(nextOf(params), { replace: true });
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }
  return <Frame>
    <header className="xo-auth-card-head"><h1>{t('sign_in_title')}</h1><p className="xo-muted">{t('sign_in_detail')}</p></header>
    <form onSubmit={submit} className="xo-form xo-auth-form">
      <label className="xo-auth-field"><span><Mail aria-hidden="true" /> {t('email')}</span>
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required disabled={busy} autoFocus /></label>
      <label className="xo-auth-field"><span><LockKeyhole aria-hidden="true" /> {t('password')}</span>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required disabled={busy} /></label>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <button type="submit" className="xo-btn xo-auth-submit" disabled={busy}>{t(busy ? 'signing_in' : 'sign_in')}</button>
      <Link className="xo-link xo-cp-forgot" to={`${BASE}/forgot`}>{t('forgot_password')}</Link>
    </form>
  </Frame>;
}

// A forgotten password: the email gets a link to choose a new one, if it has an account.
function Forgot() {
  const [email, setEmail] = useState(''), [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError('');
    try { await askPasswordReset(email); setSent(true); } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  return <Frame>
    <header className="xo-auth-card-head"><h1>{t('forgot_title')}</h1><p className="xo-muted">{t('forgot_detail')}</p></header>
    {sent ? <p className="xo-alert t-info" role="status">{t('forgot_sent', { email })}</p> : <form onSubmit={submit} className="xo-form xo-auth-form">
      <label className="xo-auth-field"><span><Mail aria-hidden="true" /> {t('email')}</span>
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required disabled={busy} autoFocus /></label>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <button type="submit" className="xo-btn xo-auth-submit" disabled={busy}>{t(busy ? 'sending' : 'forgot_send')}</button>
    </form>}
    <Link className="xo-link" to={`${BASE}/sign-in`}>{t('to_sign_in')}</Link>
  </Frame>;
}

// The link from that email: a new password, twice, and they are signed in.
function Reset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [reset, setReset] = useState(null), [failed, setFailed] = useState('');
  const [password, setPassword] = useState(''), [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  useEffect(() => { readPasswordReset(token).then(setReset).catch((caught) => setFailed(errorMessage(caught))); }, [token]);
  async function submit(event) {
    event.preventDefault();
    if (password !== again) { setError(t('passwords_differ')); return; }
    setBusy(true); setError('');
    try {
      await choosePassword(token, password);
      navigate(BASE, { replace: true });
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }
  if (failed) return <Frame><header className="xo-auth-card-head"><h1>{t('reset_title')}</h1></header>
    <p className="xo-alert t-bad" role="alert">{failed}</p><Link className="xo-link" to={`${BASE}/forgot`}>{t('reset_again')}</Link></Frame>;
  if (reset === null) return <Frame><Loading label={t('loading')} /></Frame>;
  return <Frame>
    <header className="xo-auth-card-head"><h1>{t('reset_title')}</h1><p className="xo-muted">{t('reset_detail', { email: reset.email })}</p></header>
    <form onSubmit={submit} className="xo-form xo-auth-form">
      <label className="xo-auth-field"><span><LockKeyhole aria-hidden="true" /> {t('choose_password')}</span>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={10} autoComplete="new-password" required disabled={busy} autoFocus />
        <small className="xo-muted">{t('password_hint')}</small></label>
      <label className="xo-auth-field"><span><LockKeyhole aria-hidden="true" /> {t('password_again')}</span>
        <input type="password" value={again} onChange={(event) => setAgain(event.target.value)} minLength={10} autoComplete="new-password" required disabled={busy} /></label>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <button type="submit" className="xo-btn xo-auth-submit" disabled={busy}>{t(busy ? 'saving' : 'reset_save')}</button>
    </form>
  </Frame>;
}

// The account link we emailed: their name and a password for the account, then straight in, to
// their overview, where what we asked waits. An email that has set its account up signs in instead.
function Join() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null), [failed, setFailed] = useState('');
  const [fields, setFields] = useState({ firstName: '', lastName: '', password: '', again: '' });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  useEffect(() => { readLink(token).then(setLink).catch((caught) => setFailed(errorMessage(caught))); }, [token]);
  const set = (key) => (event) => setFields((current) => ({ ...current, [key]: event.target.value }));
  async function submit(event) {
    event.preventDefault();
    if (fields.password !== fields.again) { setError(t('passwords_differ')); return; }
    setBusy(true); setError('');
    try {
      await joinWithLink(token, { firstName: fields.firstName, lastName: fields.lastName, password: fields.password });
      navigate(BASE, { replace: true });
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }
  if (failed) return <Frame><header className="xo-auth-card-head"><h1>{t('link_title')}</h1></header>
    <p className="xo-alert t-bad" role="alert">{failed}</p><Link className="xo-link" to={`${BASE}/sign-in`}>{t('to_sign_in')}</Link></Frame>;
  if (link === null) return <Frame><Loading label={t('loading')} /></Frame>;
  return <Frame>
    <header className="xo-auth-card-head"><h1>{t('join_title', { name: link.organization.name })}</h1>
      <p className="xo-muted">{link.invitedBy ? t('invited_by', { name: link.invitedBy }) : t('invited')}</p></header>
    {link.hasAccount ? <><p className="xo-alert t-info">{t('join_has_account', { email: link.email })}</p>
      <Link className="xo-btn xo-auth-submit" to={`${BASE}/sign-in`}>{t('sign_in')}</Link></> : (
      <form onSubmit={submit} className="xo-form xo-auth-form">
        <label className="xo-auth-field"><span><Mail aria-hidden="true" /> {t('email')}</span><input type="email" value={link.email} disabled readOnly /></label>
        <div className="xo-cp-two">
          <label className="xo-auth-field"><span>{t('first_name')}</span><input value={fields.firstName} onChange={set('firstName')} maxLength={128} required disabled={busy} autoFocus /></label>
          <label className="xo-auth-field"><span>{t('last_name')}</span><input value={fields.lastName} onChange={set('lastName')} maxLength={128} required disabled={busy} /></label>
        </div>
        <label className="xo-auth-field"><span><LockKeyhole aria-hidden="true" /> {t('choose_password')}</span>
          <input type="password" value={fields.password} onChange={set('password')} minLength={10} autoComplete="new-password" required disabled={busy} />
          <small className="xo-muted">{t('password_hint')}</small></label>
        <label className="xo-auth-field"><span><LockKeyhole aria-hidden="true" /> {t('password_again')}</span>
          <input type="password" value={fields.again} onChange={set('again')} minLength={10} autoComplete="new-password" required disabled={busy} /></label>
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <button type="submit" className="xo-btn xo-auth-submit" disabled={busy}>{t(busy ? 'setting_up' : 'set_up_account')}</button>
        <p className="xo-muted xo-cp-account-note">{t('account_note')}</p>
      </form>)}
  </Frame>;
}

// ------------------------------------------------------------------- the frame, signed in

const NAV = Object.freeze([
  { path: '', end: true, Icon: LayoutDashboard, key: 'nav_overview' },
  { path: 'projects', Icon: FlaskConical, key: 'nav_projects', count: 'waiting' },
  { path: 'invoices', Icon: Receipt, key: 'nav_invoices', count: 'unpaid' },
]);

function pageOf(pathname) {
  if (pathname.startsWith(`${BASE}/projects`) || pathname.startsWith(`${BASE}/submissions`)) return 'nav_projects';
  if (pathname.startsWith(`${BASE}/invoices`)) return 'nav_invoices';
  if (pathname.startsWith(`${BASE}/account`)) return 'nav_account';
  return 'nav_overview';
}

// The portal's own frame, as ours: their sidebar, the top bar with where they are and who they are.
function Shell({ me, overview, onSignOut, children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const scroller = useRef(null);
  // Each page opens at its top, where it says what just happened (a submission submitted).
  useEffect(() => { scroller.current?.scrollTo?.(0, 0); }, [pathname]);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) setMenu(false); };
    const escape = (event) => { if (event.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape); };
  }, [menu]);
  // What waits for them in their projects: one-time projects to fill in, and patient reports to answer.
  const counts = {
    waiting: overview ? overview.submissions.filter((project) => project.stage === 'requested').length + reportsToAnswer(overview.submissions).length : 0,
    unpaid: overview ? overview.invoices.filter(toPay).length : 0,
  };
  const { account, organization, pointOfContact } = me;
  return <main id="xo" className="xo-shell xo-cp-shell">
    <aside className="xo-rail">
      <div className="xo-rail-top">
        <Link to={BASE} className="xo-brand"><img src={logo} alt="" className="xo-logo-img" /><span className="xo-wordmark">{t('wordmark')}</span></Link>
      </div>
      <nav className="xo-nav" aria-label={t('nav_label')}>
        <div className="xo-nav-section">
          {NAV.map(({ path, end, Icon, key, count }) => <NavLink key={key} to={`${BASE}${path ? `/${path}` : ''}`} end={end}
            className={({ isActive }) => `xo-nav-item${isActive ? ' is-on' : ''}`}>
            <Icon aria-hidden="true" /><span>{t(key)}</span>
            {count && counts[count] > 0 && <b className="xo-cp-nav-count">{counts[count]}</b>}
          </NavLink>)}
        </div>
        <div className="xo-nav-section">
          <p className="xo-nav-label">{t('nav_you')}</p>
          <NavLink to={`${BASE}/account`} className={({ isActive }) => `xo-nav-item${isActive ? ' is-on' : ''}`}>
            <UserRound aria-hidden="true" /><span>{t('nav_account')}</span></NavLink>
        </div>
      </nav>
      {pointOfContact && <div className="xo-cp-rail-contact">
        <small>{t('your_contact')}</small>
        <strong>{pointOfContact.name}</strong>
        <span>{pointOfContact.email}</span>
      </div>}
      <div className="xo-rail-bottom">
        <div className="xo-rail-location"><Building2 aria-hidden="true" />
          <span><strong>{organization.name}</strong><small>{t('customer_line', { code: organization.code })}</small></span></div>
      </div>
    </aside>

    <div className="xo-body">
      <header className="xo-topbar">
        <div className="xo-crumbs">
          <span>{organization.name}</span><ChevronRight aria-hidden="true" /><strong>{t(pageOf(pathname))}</strong>
        </div>
        <div className="xo-topbar-actions">
          <div className="xo-menu" ref={menuRef}>
            <button type="button" className="xo-menu-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((was) => !was)}>
              <span className="xo-avatar" aria-hidden="true">{initialsOf(account.firstName, account.lastName)}</span>
              <span className="xo-menu-who"><strong>{account.firstName} {account.lastName}</strong><span>{organization.name}</span></span>
              <ChevronDown aria-hidden="true" />
            </button>
            {menu && <div className="xo-menu-panel" role="menu">
              <div className="xo-menu-head"><strong>{account.firstName} {account.lastName}</strong><span>{account.email}</span></div>
              <button type="button" role="menuitem" onClick={() => { setMenu(false); navigate(`${BASE}/account`); }}>
                <UserRound aria-hidden="true" /> {t('nav_account')}</button>
              <button type="button" role="menuitem" className="t-bad" onClick={() => { setMenu(false); onSignOut(); }}>
                <LogOut aria-hidden="true" /> {t('sign_out')}</button>
            </div>}
          </div>
        </div>
      </header>
      <div className="xo-scroll" ref={scroller}><div className="xo-page xo-cp-page">{children}</div></div>
    </div>
  </main>;
}

// A card of rows, as on our pages: a heading, a line under it and what can be done there.
function Surface({ title, detail, action, children, className = '' }) {
  return <section className={`xo-cust-surface ${className}`}>
    <header className="xo-cust-surface-head"><div><h2>{title}</h2>{detail && <p>{detail}</p>}</div>{action}</header>
    {children}
  </section>;
}

// A page's head: back (to the projects, or to the page it belongs to, named for screen readers), its
// title and the line under it, and what can be done there.
function Head({ title, detail, back = false, backLabel = null, children }) {
  const backName = backLabel ?? t('to_projects');
  return <header className="xo-cp-head">
    {back && <Link to={back} className="xo-cust-back" aria-label={backName} title={backName}><ChevronLeft aria-hidden="true" /></Link>}
    <div className="xo-cp-head-text"><h1>{title}</h1>{detail && <p>{detail}</p>}</div>
    {children && <div className="xo-head-actions">{children}</div>}
  </header>;
}

// ------------------------------------------------------------------- the pages

// What a project's row says under its workflow: a one-time project's samples asked or submitted; a
// continuous one's submissions so far.
function rowLine(project) {
  if (project.kind === 'continuous') {
    return project.submissions.length === 0 ? t('continuous_none_yet')
      : `${counted('boxes_count', project.submissions.length)} · ${t('last_on', { date: shortDay(project.lastSentAt) })}`;
  }
  return project.sentAt === null ? t('asked_short', { count: project.sampleCount, date: shortDay(project.requestedAt) })
    : t('submitted_short', { count: project.sentCount, date: shortDay(project.sentAt) });
}

// One project in a list, as our sample tracking lists a sample: what it is, where it is.
function ProjectRow({ project }) {
  const [tone, state] = stateOf(project);
  const continuous = project.kind === 'continuous';
  return <li><Link to={projectPath(project.uuid)} className={`xo-cust-sub xo-cp-sub is-${project.stage}`}>
    <span className="xo-cust-tile" aria-hidden="true">{continuous ? <Repeat /> : <FlaskConical />}</span>
    <span className="xo-cust-cell">
      <strong className="xo-cust-ref">{t('project', { number: project.number })}</strong>
      <span>{project.workflow.name}{continuous && <> · {t('kind.continuous')}</>}</span>
      <small>{rowLine(project)}</small>
    </span>
    <span className="xo-cust-sub-journey">{continuous
      ? <span className="xo-cust-continuous"><span className="xo-cust-continuous-line">{counted('samples_sent', project.sentCount)}</span>
        {project.sentCount > 0 && <ProgressBar progress={project.progress} total={project.sentCount} />}</span>
      : <Journey submission={project} />}</span>
    <span className="xo-cust-cell is-money"><Pill tone={tone}>{t(state)}</Pill>
      {project.qcWaiting > 0 && <Pill tone="warn">{t('qc_to_answer')}</Pill>}</span>
    <ChevronRight className="xo-cust-open" aria-hidden="true" />
  </Link></li>;
}

// What needs them: a project to fill in, QC results to answer, patient reports to answer, a pro
// forma invoice to look over, an invoice to pay.
function Tasks({ projects, qc, reports, proformas, invoices, onError }) {
  if (projects.length + qc.length + reports.length + proformas.length + invoices.length === 0) return <p className="xo-cust-quiet">{t('nothing_needs_you')}</p>;
  return <ol className="xo-cust-rows">
    {qc.map(({ project, box }) => <li key={`qc-${project.uuid}-${box.number}`}><div className="xo-cp-task">
      <span className="xo-cust-tile is-ask" aria-hidden="true"><Microscope /></span>
      <span className="xo-cust-cell">
        <strong>{t('task_qc')}</strong>
        <small>{counted('task_qc_line', box.qcWaiting)} · {project.kind === 'continuous' ? t('box_label', { project: project.number, number: box.number })
          : t('project', { number: project.number })}</small>
      </span>
      <Link className="xo-btn" to={boxLink(project, box)}>{t('answer')}<ArrowRight aria-hidden="true" /></Link>
    </div></li>)}
    {projects.map((project) => <li key={project.uuid}><div className="xo-cp-task">
      <span className="xo-cust-tile is-ask" aria-hidden="true"><FlaskConical /></span>
      <span className="xo-cust-cell">
        <strong>{t('task_send', { workflow: project.workflow.name })}</strong>
        <small>{t('task_send_line_project', { number: project.number, count: project.sampleCount, date: day(project.requestedAt) })}</small>
      </span>
      <Link className="xo-btn" to={projectPath(project.uuid)}>{t('fill_in')}<ArrowRight aria-hidden="true" /></Link>
    </div></li>)}
    {reports.map(({ project, box }) => <li key={`${project.uuid}-${box.number}`}><div className="xo-cp-task">
      <span className="xo-cust-tile is-review" aria-hidden="true"><FileText /></span>
      <span className="xo-cust-cell">
        <strong>{t('task_reports')}</strong>
        <small>{t('task_reports_line', { project: project.number, number: box.number, workflow: project.workflow.name })}</small>
      </span>
      <Link className="xo-btn-2" to={boxLink(project, box)}>{t('answer')}<ArrowRight aria-hidden="true" /></Link>
    </div></li>)}
    {proformas.map((invoice) => <li key={invoice.uuid}><div className="xo-cp-task">
      <span className="xo-cust-tile is-review" aria-hidden="true"><FileSearch /></span>
      <span className="xo-cust-cell">
        <strong>{t('task_review', { number: invoice.number })}</strong>
        <small>{amount(invoice.amountMinor, invoice.currency, invoice.minorUnit)} · {t('nothing_to_pay_yet')} · {t('for_project', { number: invoice.submission.number })}</small>
      </span>
      <button type="button" className="xo-btn-2" onClick={() => openDocument('invoice', invoice.uuid).catch((caught) => onError(errorMessage(caught)))}>
        {t('view_proforma')}</button>
    </div></li>)}
    {invoices.map((invoice) => <li key={invoice.uuid}><div className="xo-cp-task">
      <span className={`xo-cust-tile ${invoice.status === 'overdue' ? 'is-bad' : 'is-due'}`} aria-hidden="true"><Receipt /></span>
      <span className="xo-cust-cell">
        <strong>{t('task_pay', { number: invoice.number })}{invoice.status === 'overdue' && <> <Pill tone="bad">{t('invoice_status.overdue')}</Pill></>}</strong>
        <small>{amount(invoice.amountMinor, invoice.currency, invoice.minorUnit)} · {t('due_on', { date: day(invoice.dueAt) })} · {t('for_project', { number: invoice.submission.number })}</small>
      </span>
      <button type="button" className="xo-btn-2" onClick={() => openDocument('invoice', invoice.uuid).catch((caught) => onError(errorMessage(caught)))}>
        {t('view_invoice')}</button>
    </div></li>)}
  </ol>;
}

// Our laboratory where a project's samples go, with its address.
function ShipTo({ place, title = null }) {
  if (!place) return null;
  return <div className="xo-cp-place">
    <span className="xo-cp-place-icon" aria-hidden="true"><MapPin /></span>
    <div>
      {title && <small>{title}</small>}
      <strong>{place.name}</strong>
      <address>
        {place.addressLine1}<br />
        {place.addressLine2 && <>{place.addressLine2}<br /></>}
        {[place.postalCode, place.city].filter(Boolean).join(' ')}{place.region ? `, ${place.region}` : ''}<br />
        {countryOf(place.countryCode)}
      </address>
      {(place.phone || place.email) && <p className="xo-cp-place-reach">
        {place.phone && <span><Phone aria-hidden="true" />{place.phone}</span>}
        {place.email && <span><Mail aria-hidden="true" />{place.email}</span>}
      </p>}
    </div>
  </div>;
}

// Their contact at Phenome. `phoneOnly`: the sidebar shows them already, so the card is for a phone,
// where the sidebar keeps its icons only.
function ContactCard({ me, phoneOnly = false }) {
  if (!me.pointOfContact) return null;
  const [first, ...rest] = me.pointOfContact.name.split(' ');
  return <section className={`xo-cp-card${phoneOnly ? ' xo-cp-phone-only' : ''}`}>
    <h3>{t('your_contact')}</h3>
    <div className="xo-cp-person">
      <span className="xo-cust-avatar" aria-hidden="true">{initialsOf(first, rest.join(' '))}</span>
      <span><strong>{me.pointOfContact.name}</strong><a href={`mailto:${me.pointOfContact.email}`}>{me.pointOfContact.email}</a></span>
    </div>
    <p className="xo-cp-card-note">{t('contact_note_projects')}</p>
  </section>;
}

// Their overview: what needs them (projects to fill in, patient reports to answer, the pro forma of
// each project that waits for their samples, invoices to pay), their projects, and who to ask.
// Where samples go is not here: it belongs to each project and is on its own page.
function Overview({ me, overview, onError }) {
  const { submissions: projects, invoices } = overview;
  const asked = projects.filter((project) => project.stage === 'requested');
  const waiting = new Set(asked.map((project) => project.uuid));
  const proformas = invoices.filter((invoice) => invoice.status === 'proforma' && waiting.has(invoice.submission.uuid));
  const unpaid = invoices.filter(toPay);
  // One column, as wide as the page: what needs them, their projects, and how it works in four steps
  // side by side. Their contact is in the sidebar; on a phone, where the sidebar keeps its icons only,
  // the contact's card shows here instead.
  return <>
    <Head title={t('welcome', { name: me.account.firstName })} detail={t('overview_line', { name: me.organization.name })} />
    <div className="xo-cp-col">
      <Surface title={t('needs_you')} detail={t('needs_you_detail_projects')}>
        <Tasks projects={asked} qc={qcToAnswer(projects)} reports={reportsToAnswer(projects)} proformas={proformas} invoices={unpaid} onError={onError} />
      </Surface>
      <Surface title={t('your_projects')} detail={t('projects_detail')}
        action={projects.length > 0 && <Link className="xo-btn-2" to={`${BASE}/projects`}>{t('see_all')}</Link>}>
        {projects.length === 0 ? <div className="xo-cust-empty is-flat"><Empty title={t('no_projects_yet')} detail={t('no_projects_detail')} /></div>
          : <ol className="xo-cust-rows">{projects.slice(0, 4).map((project) => <ProjectRow key={project.uuid} project={project} />)}</ol>}
      </Surface>
      <section className="xo-cp-card">
        <h3>{t('how_it_works')}</h3>
        <ol className="xo-cp-how is-row">
          {['how_p1', 'how_p2', 'how_p3', 'how_p4'].map((key, index) => <li key={key}><b>{index + 1}</b><span>{t(key)}</span></li>)}
        </ol>
      </section>
      <ContactCard me={me} phoneOnly />
    </div>
  </>;
}

const FILTERS = Object.freeze({
  all: () => true,
  waiting: (project) => project.stage === 'requested',
  working: (project) => ['sent', 'in_lab', 'open'].includes(project.stage),
  done: (project) => project.stage === 'done' || project.stage === 'closed',
});

function Projects({ overview }) {
  const [filter, setFilter] = useState('all');
  const projects = overview.submissions;
  const shown = projects.filter(FILTERS[filter]);
  return <>
    <Head title={t('nav_projects')} detail={t('projects_page_detail')} />
    <Surface title={t('your_projects')} detail={t('projects_detail')}
      action={<WorkspaceTabs className="xo-cust-filter" value={filter} onChange={setFilter} label={t('filter')}
        tabs={Object.keys(FILTERS).map((value) => ({ value, label: t(`filter_${value}`), count: projects.filter(FILTERS[value]).length }))} />}>
      {projects.length === 0 ? <div className="xo-cust-empty is-flat"><Empty title={t('no_projects_yet')} detail={t('no_projects_detail')} /></div>
        : shown.length === 0 ? <p className="xo-cust-quiet">{t('none_here')}</p>
          : <ol className="xo-cust-rows">{shown.map((project) => <ProjectRow key={project.uuid} project={project} />)}</ol>}
    </Surface>
  </>;
}

function InvoiceRows({ invoices, onError }) {
  return <ol className="xo-cust-rows">{invoices.map((invoice) => <li key={invoice.uuid}>
    <div className={`xo-cust-invoice-row xo-cp-invoice is-${invoice.status}`}>
      <span className="xo-cust-tile" aria-hidden="true">{invoice.kind === 'proforma' ? <FileSearch /> : <Receipt />}</span>
      <span className="xo-cust-cell"><strong className="xo-cust-ref">{invoice.number}</strong>
        <small>{t('invoice_for_project', { number: invoice.submission.number, workflow: invoice.submission.workflowName })}</small></span>
      <span className="xo-cust-cell is-money"><strong>{amount(invoice.amountMinor, invoice.currency, invoice.minorUnit)}</strong><small>{billsLine(invoice)}</small></span>
      <span className="xo-cust-cell"><Pill tone={INVOICE_TONES[invoice.status]}>{t(`invoice_status.${invoice.status}`)}</Pill><small>{invoiceWhen(invoice)}</small></span>
      <span className="xo-cust-actions">
        <button type="button" className="xo-btn-2" onClick={() => openDocument('invoice', invoice.uuid).catch((caught) => onError(errorMessage(caught)))}>
          {t('view_invoice')}</button>
      </span>
    </div>
  </li>)}</ol>;
}

// Their invoices, one row each as it is: its amount in its own currency, and whether it is due,
// overdue or paid, or a pro forma (nothing to pay on it). Each invoice is paid on its own.
function Invoices({ overview, onError }) {
  const { invoices } = overview;
  return <>
    <Head title={t('nav_invoices')} detail={t('invoices_page_detail_projects')} />
    <Surface title={t('nav_invoices')} detail={t('how_to_pay')}>
      {invoices.length === 0 ? <p className="xo-cust-quiet">{t('no_invoices')}</p> : <InvoiceRows invoices={invoices} onError={onError} />}
    </Surface>
  </>;
}

function Account({ me, onSignOut }) {
  const [sent, setSent] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  async function sendLink() {
    setBusy(true); setError('');
    try { await askPasswordReset(me.account.email); setSent(true); } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  return <>
    <Head title={t('nav_account')} detail={t('account_detail')} />
    <div className="xo-cp-col">
      <section className="xo-cp-card">
        <div className="xo-cp-identity">
          <span className="xo-cust-initials" aria-hidden="true">{initialsOf(me.account.firstName, me.account.lastName)}</span>
          <div><h2>{me.account.firstName} {me.account.lastName}</h2><p>{me.account.email}</p></div>
        </div>
        <dl className="xo-cust-facts">
          <div><dt>{t('organization')}</dt><dd>{me.organization.name}<small>{t('customer_line', { code: me.organization.code })}</small></dd></div>
          <div><dt>{t('email')}</dt><dd>{me.account.email}</dd></div>
        </dl>
      </section>
      <section className="xo-cp-card">
        <h3><KeyRound aria-hidden="true" />{t('password')}</h3>
        <p className="xo-cp-card-note">{t('password_change_detail')}</p>
        {sent ? <p className="xo-alert t-info" role="status">{t('forgot_sent', { email: me.account.email })}</p>
          : <div><button type="button" className="xo-btn-2" disabled={busy} onClick={sendLink}><Mail aria-hidden="true" />{t('password_change_send')}</button></div>}
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      </section>
      <ContactCard me={me} phoneOnly />
      <div><button type="button" className="xo-btn-2 t-bad" onClick={onSignOut}><LogOut aria-hidden="true" />{t('sign_out')}</button></div>
    </div>
  </>;
}

// What we asked of a project, as wide as the page in one card: the workflow and the samples, what it
// costs (a one-time project's samples at the price for each, its other costs and the total; a
// continuous one's price for each sample, its other costs and its samples so far), and, while boxes
// are still to come, where they go.
function ProjectFacts({ project, shipTo = false }) {
  const sum = (minor) => amount(minor, project.currency, project.minorUnit);
  const continuous = project.kind === 'continuous';
  return <section className="xo-cp-card xo-cp-facts">
    <h3>{t('what_we_asked')}</h3>
    <div className="xo-cp-facts-row">
      <dl className="xo-cp-asked">
        <div><dt>{t('workflow')}</dt><dd>{project.workflow.name}</dd></div>
        <div><dt>{t('samples_up_to_label')}</dt><dd>{continuous ? t('samples_any')
          : project.submissions.length === 0 ? t('samples_up_to', { count: project.sampleCount }) : project.sentCount}</dd></div>
      </dl>
      <div>
        <table className="xo-cust-price">
          <tbody>
            <tr><td>{continuous ? t('price_each_line') : t('quote_line', { count: project.sampleCount, each: sum(project.unitPriceMinor) })}</td>
              <td>{sum(continuous ? project.unitPriceMinor : project.processingMinor)}</td></tr>
            {project.costs.map((cost, index) => <tr key={index}><td>{cost.description}</td><td>{sum(cost.amountMinor)}</td></tr>)}
          </tbody>
          {!continuous && <tfoot><tr><td>{t('total')}</td><td>{sum(project.totalMinor)}</td></tr></tfoot>}
        </table>
        {continuous && <p className="xo-cp-card-note">{t('continuous_price_note', { count: project.sentCount, amount: sum(project.processingMinor) })}</p>}
      </div>
      {shipTo && <ShipTo place={project.shipTo} title={t('ship_to')} />}
    </div>
  </section>;
}

function InvoicesCard({ project, onError }) {
  return <section className="xo-cp-card">
    <h3>{t('submission_invoices', { count: project.invoices.length })}</h3>
    {project.invoices.length === 0 ? <p className="xo-cp-card-note">{t('no_invoices_on_project')}</p>
      : <ul className="xo-cp-mini">{project.invoices.map((invoice) => <li key={invoice.uuid}>
        <span><strong>{invoice.number}</strong><small>{amount(invoice.amountMinor, invoice.currency, invoice.minorUnit)} · {invoiceWhen(invoice)}</small></span>
        <Pill tone={INVOICE_TONES[invoice.status]}>{t(`invoice_status.${invoice.status}`)}</Pill>
        <button type="button" className="xo-icon-btn" aria-label={t('view_invoice_named', { number: invoice.number })} title={t('view_invoice')}
          onClick={() => openDocument('invoice', invoice.uuid).catch((caught) => onError(errorMessage(caught)))}><ArrowRight aria-hidden="true" /></button>
      </li>)}</ul>}
  </section>;
}

function Step({ number, title, detail, state = 'open', children }) {
  return <section className={`xo-cp-step is-${state}`}>
    <header><span className="xo-cp-step-mark" aria-hidden="true">{state === 'done' ? <Check /> : number}</span>
      <div><h2>{title}</h2>{detail && <p>{detail}</p>}</div></header>
    <div className="xo-cp-step-body">{children}</div>
  </section>;
}

// What they write on a box: its project, and for a continuous one its submission too.
const boxLabel = (project, number) => (project.kind === 'continuous' ? t('box_label', { project: project.number, number }) : t('project', { number: project.number }));

// A project's next submission, filled in with the submission form: what they send (the workflow,
// and for a one-time project the number of samples, as we asked; not theirs to change; the sample
// type), their sample form (the template, and the filled one read back with every problem by its
// row), where the box goes, then submitted.
function SubmissionForm({ me, project, workflow, onSubmitted }) {
  const continuous = project.kind === 'continuous';
  const next = project.submissions.length + 1;
  // The only sample type the workflow takes is chosen for them.
  const [sampleType, setSampleType] = useState(workflow && workflow.sampleTypes.length === 1 ? workflow.sampleTypes[0].code : '');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null), [checked, setChecked] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [dragging, setDragging] = useState(false);
  // Other files sent with it: index sample sheets, library index sequences, anything else we should have.
  const [extras, setExtras] = useState([]);
  const input = useRef(null), extraInput = useRef(null);
  function addExtras(list) {
    const big = list.filter((entry) => entry.size > MAX_FILE_BYTES);
    setError(big.map((entry) => t('file_too_large', { name: entry.name })).join(' · '));
    setExtras((current) => [...current, ...list.filter((entry) => entry.size <= MAX_FILE_BYTES)]);
  }
  // The file is read for the sample type chosen, which says which columns its samples need.
  async function check(upload, type) {
    setBusy(true); setError(''); setChecked(null);
    try {
      setChecked(await checkSamples(project.uuid, upload, type));
    } catch (caught) { setFile(null); setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  async function read(chosen) {
    if (!chosen || !workflow) return;
    setError('');
    try {
      const upload = await fileForUpload(chosen);
      setFile(upload);
      await check(upload, sampleType);
    } catch (caught) { setFile(null); setError(errorMessage(caught)); }
  }
  function chooseType(type) {
    setSampleType(type);
    if (file) check(file, type);
  }
  // The sample types some columns are needed for, of those this workflow takes.
  const neededFor = workflow ? workflow.sampleTypes.filter((type) => workflow.columns.some((column) => column.requiredFor.includes(type.code))) : [];
  // Sent, then each other file added to the new submission; one that could not be added is said on its page.
  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError('');
    let made;
    try {
      made = await sendSamples(project.uuid, { sampleTypeCode: sampleType, file, note: note.trim() || null });
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); return; }
    const problems = [];
    for (const extra of extras) {
      try { made = { ...made, submission: (await addSubmissionFiles(project.uuid, made.sent, [await fileForUpload(extra)])).submission }; }
      catch (caught) { problems.push(`${extra.name}: ${errorMessage(caught)}`); }
    }
    onSubmitted(made, problems);
  }
  const good = checked && checked.problems.length === 0 && checked.samples.length > 0;
  const ready = workflow && sampleType && good;
  const missing = [!sampleType && t('missing_type'), !good && t('missing_form')].filter(Boolean);
  const count = checked?.samples.length || 0;
  return <form className="xo-cp-col" onSubmit={submit}>
    <Step number="1" title={t('step1_title')} detail={t('step1_detail')} state={sampleType ? 'done' : 'open'}>
      {project.requestNote && <div className="xo-cp-note-card">
        <MessageSquareText aria-hidden="true" />
        <div><small>{project.requestedBy ? t('note_from', { name: project.requestedBy }) : t('note_from_us')}</small><p>{project.requestNote}</p></div>
      </div>}
      <div className="xo-cp-two is-three">
        <div className="xo-cp-fixed"><span>{t('workflow')}</span><strong><Lock aria-hidden="true" />{project.workflow.name}</strong></div>
        <div className="xo-cp-fixed"><span>{t('samples_up_to_label')}</span><strong><Lock aria-hidden="true" />
          {continuous ? t('samples_any') : t('samples_up_to', { count: project.sampleCount })}</strong></div>
        <div className="xo-cp-fixed"><span>{t('price_each_line')}</span><strong><Lock aria-hidden="true" />
          {amount(project.unitPriceMinor, project.currency, project.minorUnit)}</strong></div>
      </div>
      <p className="xo-cp-hint">{t(continuous ? 'fixed_hint_continuous' : 'fixed_hint_project')}</p>
      <label className="xo-cp-field"><span>{t('sample_type')}</span>
        <XomicsSelect value={sampleType} onChange={(event) => chooseType(event.target.value)} disabled={busy || !workflow}>
          <option value="">{t('choose_sample_type')}</option>
          {(workflow?.sampleTypes || []).map((type) => <option key={type.code} value={type.code}>{type.name}</option>)}
        </XomicsSelect></label>
    </Step>

    <Step number="2" title={t('step2_title')} detail={[t('step2_detail_lab_form'),
      neededFor.length > 0 && t('step2_detail_needed_for', { types: neededFor.map((type) => type.name).join(', ') })].filter(Boolean).join(' ')}
      state={good ? 'done' : 'open'}>
      <div className="xo-cp-template">
        <span><FileSpreadsheet aria-hidden="true" /><span><strong>{t('template_title_lab')}</strong>
          <small>{continuous ? t('template_line_any') : t('template_line', { count: project.sampleCount })}</small></span></span>
        <div>
          <button type="button" className="xo-btn-2" disabled={!workflow}
            onClick={() => downloadTemplate(workflow, 'xlsx', project.uuid).catch((caught) => setError(errorMessage(caught)))}>
            <Download aria-hidden="true" />{t('template_get_excel')}</button>
          <button type="button" className="xo-btn-2" disabled={!workflow} onClick={() => downloadTemplate(workflow, 'csv').catch((caught) => setError(errorMessage(caught)))}>
            {t('template_get_csv')}</button>
        </div>
      </div>
      <input ref={input} type="file" accept=".xlsx,.csv,text/csv" hidden onChange={(event) => { const chosen = event.target.files?.[0]; event.target.value = ''; read(chosen); }} />
      {file === null ? <button type="button" className={`xo-cp-drop${dragging ? ' is-over' : ''}`} disabled={!workflow || busy}
        onClick={() => input.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
        onDrop={(event) => { event.preventDefault(); setDragging(false); read(event.dataTransfer.files?.[0]); }}>
        <Upload aria-hidden="true" /><strong>{t(busy ? 'reading_form' : 'drop_title')}</strong><small>{t(continuous ? 'drop_detail_any' : 'drop_detail')}</small>
      </button> : <div className={`xo-cp-file${checked && checked.problems.length > 0 ? ' is-bad' : good ? ' is-ok' : ''}`}>
        <FileSpreadsheet aria-hidden="true" />
        <span><strong>{file.name}</strong><small>{busy ? t('reading_form') : checked ? (checked.problems.length > 0 ? counted('problems_count', checked.problems.length)
          : continuous ? counted('samples_read', count) : t('samples_read_of', { count, asked: project.sampleCount })) : ''}</small></span>
        <button type="button" className="xo-btn-2" disabled={busy} onClick={() => input.current?.click()}>{t('choose_other_file')}</button>
      </div>}
      {checked && checked.problems.length > 0 && <div className="xo-alert t-bad" role="alert"><strong>{t('problems', { count: checked.problems.length })}</strong>
        <ul className="xo-cp-problems">{checked.problems.map((problem, index) => <li key={index}>{t('row', { line: problem.line })}: {problem.message}</li>)}</ul></div>}
      {checked && checked.samples.length > 0 && <SampleAnswersTable columns={workflow.columns} samples={checked.samples}
        lead={[{ key: 'n', label: '#', cell: (_sample, index) => index + 1 }, { key: 'name', label: t('sample_name'), cell: (sample) => <strong>{sample.sampleName}</strong> }]} />}
      <div className="xo-cp-extras">
        <div><strong>{t('extras_title')}</strong><small>{t('extras_detail')}</small></div>
        {extras.length > 0 && <ul>{extras.map((extra, index) => <li key={`${extra.name}-${index}`}>
          <FileText aria-hidden="true" /><span>{extra.name}</span><small>{fileSize(extra.size)}</small>
          <button type="button" className="xo-icon-btn" aria-label={t('file_remove_named', { name: extra.name })} disabled={busy}
            onClick={() => setExtras((current) => current.filter((_, at) => at !== index))}><X aria-hidden="true" /></button>
        </li>)}</ul>}
        <input ref={extraInput} type="file" multiple hidden accept={FILE_ACCEPT}
          onChange={(event) => { const list = [...(event.target.files || [])]; event.target.value = ''; addExtras(list); }} />
        <button type="button" className="xo-btn-2" disabled={busy} onClick={() => extraInput.current?.click()}><Paperclip aria-hidden="true" />{t('extras_add')}</button>
      </div>
    </Step>

    <Step number="3" title={t('step3_title')} detail={t('step3_detail')} state="open">
      <ShipTo place={project.shipTo} />
      <ul className="xo-cp-checklist">
        <li><Check aria-hidden="true" />{t('pack_1')}</li>
        <li><Check aria-hidden="true" />{t('pack_2_label', { label: boxLabel(project, next) })}</li>
        <li><Check aria-hidden="true" />{t('pack_3')}</li>
      </ul>
    </Step>

    <Step number="4" title={t('step4_title')} detail={me.pointOfContact ? t('step4_detail', { name: me.pointOfContact.name }) : t('step4_detail_plain')}
      state={ready ? 'ready' : 'waiting'}>
      <label className="xo-cp-field"><span>{t('note')}</span>
        <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} rows={3} disabled={busy} placeholder={t('note_placeholder')} /></label>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <div className="xo-cp-submit">
        <button type="submit" className="xo-btn" disabled={!ready || busy}><Package aria-hidden="true" />{t(busy ? 'submitting' : 'submit_samples', { count })}</button>
        {!ready && missing.length > 0 && <small>{t('still_missing', { what: missing.join(', ') })}</small>}
      </div>
    </Step>
  </form>;
}

// A submission's list of samples, their names beside our codes, as a file to put in the box.
function downloadList(project, box) {
  const cell = (text) => (/[",\n]/.test(String(text)) ? `"${String(text).replace(/"/g, '""')}"` : String(text));
  const rows = [[t('sample_name'), t('our_code')], ...box.samples.map((sample) => [sample.sampleName, sample.code])];
  const url = URL.createObjectURL(new Blob([`﻿${rows.map((row) => row.map(cell).join(',')).join('\r\n')}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = project.kind === 'continuous' ? `project-${project.number}-submission-${box.number}.csv` : `project-${project.number}.csv`;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// A sample's patient reports, for them: the person as they gave them, and each report, ready to
// download once our doctors approved the results, being checked by them, being made, held back, or
// not made; waiting for its analysis while there is none yet.
function ReportCell({ sample, onError }) {
  if (!sample.patientReport) return <span className="xo-muted">—</span>;
  const { person, reports } = sample.patientReport;
  return <span className="xo-cp-report-cell">
    {person && <strong>{person.fullName}</strong>}
    {reports.length === 0 ? <small>{t('report_waiting')}</small> : reports.map((report) => (report.status === 'ready'
      ? <button type="button" key={report.uuid} className="xo-btn-2 xo-cp-report-btn" onClick={() => openPatientReport(report.uuid).catch((caught) => onError(errorMessage(caught)))}>
        <Download aria-hidden="true" />{report.type.name}</button>
      : <Pill key={report.uuid} tone={REPORT_TONES[report.status]}>{report.type.name}: {t(`report_status.${report.status}`)}</Pill>))}
  </span>;
}

// Their answer on patient reports, given with the person of each sample that is done: their full
// name, sex and date of birth, and the language of the reports. Every detail is needed; a wrong one
// is marked where it is.
function ReportsForm({ project, box, languages, onCancel, onDone }) {
  const samples = box.samples.filter((sample) => sample.status === 'completed');
  const stopped = box.samples.length - samples.length;
  const [language, setLanguage] = useState(languages.some((entry) => entry.code === 'en') ? 'en' : (languages[0]?.code ?? ''));
  const [people, setPeople] = useState(() => Object.fromEntries(samples.map((sample) => [sample.position, { fullName: '', sex: '', dateOfBirth: '' }])));
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [wrong, setWrong] = useState(() => new Set());
  const set = (position, field) => (event) => setPeople((current) => ({ ...current, [position]: { ...current[position], [field]: event.target.value } }));
  const today = new Date().toISOString().slice(0, 10);
  const complete = language !== '' && samples.every((sample) => {
    const person = people[sample.position];
    return person.fullName.trim() !== '' && person.sex !== '' && person.dateOfBirth !== '';
  });
  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(''); setWrong(new Set());
    try {
      const made = await askPatientReports(project.uuid, box.number, { languageCode: language,
        people: samples.map((sample) => ({ position: sample.position, ...people[sample.position], fullName: people[sample.position].fullName.trim() })) });
      onDone(made.submission);
    } catch (caught) {
      const problems = caught.body?.details?.problems;
      if (Array.isArray(problems)) setWrong(new Set(problems.map((problem) => `${problem.position}:${problem.field}`)));
      setError(errorMessage(caught)); setBusy(false);
    }
  }
  const isWrong = (position, field) => wrong.has(`${position}:${field}`) || undefined;
  return <form className="xo-cp-card xo-cp-reports is-form" onSubmit={submit}>
    <h3><FileText aria-hidden="true" />{t('reports_form_title')}</h3>
    <p className="xo-cp-card-note">{t('reports_form_detail')}</p>
    <label className="xo-cp-field xo-cp-reports-language"><span>{t('reports_language')}</span>
      <XomicsSelect value={language} onChange={(event) => setLanguage(event.target.value)} disabled={busy}>
        {languages.map((entry) => <option key={entry.code} value={entry.code}>{entry.nativeName && entry.nativeName !== entry.name ? `${entry.name} · ${entry.nativeName}` : entry.name}</option>)}
      </XomicsSelect></label>
    <div className="xo-cust-table-wrap is-flat"><table className="xo-cust-table xo-cp-people">
      <thead><tr><th>{t('sample_name')}</th><th>{t('our_code')}</th><th>{t('person_name')}</th><th>{t('person_sex')}</th><th>{t('person_birth')}</th></tr></thead>
      <tbody>{samples.map((sample) => {
        const person = people[sample.position];
        return <tr key={sample.position}><td>{sample.sampleName}</td><td><code>{sample.code}</code></td>
          <td><input value={person.fullName} onChange={set(sample.position, 'fullName')} maxLength={255} disabled={busy} autoComplete="off"
            aria-label={t('person_name_of', { name: sample.sampleName })} aria-invalid={isWrong(sample.position, 'fullName')} /></td>
          <td><XomicsSelect value={person.sex} onChange={set(sample.position, 'sex')} disabled={busy} aria-label={t('person_sex_of', { name: sample.sampleName })}
            aria-invalid={isWrong(sample.position, 'sex')}>
            <option value="">{t('choose')}</option><option value="female">{t('sex.female')}</option><option value="male">{t('sex.male')}</option>
          </XomicsSelect></td>
          <td><input type="date" value={person.dateOfBirth} min="1900-01-01" max={today} onChange={set(sample.position, 'dateOfBirth')} disabled={busy}
            aria-label={t('person_birth_of', { name: sample.sampleName })} aria-invalid={isWrong(sample.position, 'dateOfBirth')} /></td></tr>;
      })}</tbody>
    </table></div>
    {stopped > 0 && <p className="xo-cp-card-note">{counted('reports_stopped', stopped)}</p>}
    <p className="xo-cp-card-note xo-cp-reports-privacy"><Lock aria-hidden="true" />{t('reports_privacy')}</p>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-cp-reports-actions">
      <button type="button" className="xo-btn-2" onClick={onCancel} disabled={busy}>{t('cancel')}</button>
      <button type="submit" className="xo-btn" disabled={busy || !complete}><FileText aria-hidden="true" />
        {busy ? t('sending') : counted('reports_send', samples.length)}</button>
    </div>
  </form>;
}

// Patient reports, at the very end of a submission (the laboratory done with its samples, for a
// workflow that has reports): would they like one for each person; yes opens the form for the
// people, no is kept and can still become a yes. Once asked, who asked and in which language; each
// report is in the samples' list.
function PatientReports({ project, box, languages, onUpdated }) {
  const [filling, setFilling] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const answer = box.reports?.answer ?? null;
  if (answer === 'wanted') {
    return <section className="xo-cp-card xo-cp-reports is-asked">
      <span className="xo-cp-reports-icon" aria-hidden="true"><FileText /></span>
      <div><h3>{t('reports_title')}</h3>
        <p>{t('reports_asked_line', { name: box.reports.answeredBy || '', date: day(box.reports.answeredAt), language: box.reports.language?.name || '' })}</p>
        <p className="xo-cp-card-note">{t('reports_asked_note_checked')}</p></div>
    </section>;
  }
  if (!box.reportsOffered) return null;
  if (filling) return <ReportsForm project={project} box={box} languages={languages} onCancel={() => setFilling(false)} onDone={(next) => { setFilling(false); onUpdated(next); }} />;
  async function decline() {
    setBusy(true); setError('');
    try { onUpdated((await declinePatientReports(project.uuid, box.number)).submission); } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  if (answer === 'declined') {
    return <section className="xo-cp-card xo-cp-reports is-declined">
      <p>{t('reports_declined', { date: day(box.reports.answeredAt) })}</p>
      <button type="button" className="xo-btn-2" onClick={() => setFilling(true)}>{t('reports_change')}</button>
    </section>;
  }
  const done = box.samples.filter((sample) => sample.status === 'completed').length;
  return <section className="xo-cp-card xo-cp-reports is-offer">
    <span className="xo-cp-reports-icon" aria-hidden="true"><FileText /></span>
    <div>
      <h3>{t('reports_title')}</h3>
      <p>{counted('reports_offer', done)}</p>
      <p className="xo-cp-card-note">{t('reports_types', { types: project.reportTypes.map((type) => type.name).join(', ') })}</p>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <div className="xo-cp-reports-actions">
        <button type="button" className="xo-btn-2" onClick={decline} disabled={busy}>{t('reports_no')}</button>
        <button type="button" className="xo-btn" onClick={() => setFilling(true)} disabled={busy}>{t('reports_yes')}</button>
      </div>
    </div>
  </section>;
}

// Our first quality check of a submission's samples, a row each (QcTable): what we measured with what
// they wrote under it, passed or not, and their answer: go on, or stop (once, for each sample that
// waits for it; all of them at once if they like), sent from one bar with a note to us. What the
// laboratory said with the results on top.
function QcCard({ project, box, columns, compare, onUpdated }) {
  const [chosen, setChosen] = useState({}), [note, setNote] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const checked = box.samples.filter((sample) => sample.qc !== null);
  if (checked.length === 0) return null;
  const waiting = checked.filter((sample) => sample.qc.waiting);
  const answers = waiting.filter((sample) => chosen[sample.position]).map((sample) => ({ position: sample.position, answer: chosen[sample.position] }));
  const stops = answers.filter((entry) => entry.answer === 'stop').length;
  const choose = (position, answer) => setChosen((current) => ({ ...current, [position]: current[position] === answer ? undefined : answer }));
  async function send() {
    setBusy(true); setError('');
    try {
      const made = await answerQc(project.uuid, box.number, { answers, note: note.trim() || null });
      setChosen({}); setNote('');
      onUpdated(made.submission);
    } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  return <section className="xo-cp-card xo-cp-qc" id="qc-results">
    <header className="xo-cp-qc-head">
      <span className="xo-cp-reports-icon" aria-hidden="true"><Microscope /></span>
      <div><h3>{t('qc_title')}</h3><p>{waiting.length > 0 ? counted('qc_waiting_line', waiting.length) : t('qc_done_line')}</p></div>
    </header>
    {box.qcSent?.message && <p className="xo-cp-qc-message"><small>{t('qc_message_from', { date: day(box.qcSent.at) })}</small>{box.qcSent.message}</p>}
    <QcTable samples={box.samples} compare={compare} columns={columns} chosen={chosen} onChoose={choose} busy={busy} />
    {waiting.length > 0 && <footer className="xo-cp-qc-bar">
      <span className="xo-cp-qc-tally">{answers.length === 0 ? t('qc_tally_none')
        : [answers.length - stops > 0 && counted('qc_tally_go', answers.length - stops), stops > 0 && counted('qc_tally_stop', stops)].filter(Boolean).join(' · ')}</span>
      <input className="xo-cp-qc-note" value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} disabled={busy}
        placeholder={t('qc_note_placeholder')} aria-label={t('qc_note')} />
      <button type="button" className="xo-btn-2" disabled={busy}
        onClick={() => setChosen(Object.fromEntries(waiting.map((sample) => [sample.position, 'proceed'])))}>{t('qc_all_go_on')}</button>
      <button type="button" className="xo-btn" disabled={busy || answers.length === 0} onClick={send}>
        {busy ? t('sending') : answers.length === 0 ? t('qc_send_none') : counted('qc_send', answers.length)}</button>
    </footer>}
    {stops > 0 && <p className="xo-cp-qc-warning" role="status"><CircleSlash aria-hidden="true" />{counted('qc_stop_warning', stops)}</p>}
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </section>;
}

// The files of a submission: our laboratory's (its QC reports) to open, and theirs (index sample
// sheets, library index sequences, anything else we should have): opened, added, taken away again.
function FilesCard({ project, box, onUpdated }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const input = useRef(null);
  const ours = box.files.filter((file) => file.side === 'laboratory');
  const theirs = box.files.filter((file) => file.side === 'customer');
  async function add(list) {
    setBusy(true); setError('');
    const problems = [];
    let latest = null;
    for (const file of list) {
      if (file.size > MAX_FILE_BYTES) { problems.push(t('file_too_large', { name: file.name })); continue; }
      try { latest = await addSubmissionFiles(project.uuid, box.number, [await fileForUpload(file)]); } catch (caught) { problems.push(`${file.name}: ${errorMessage(caught)}`); }
    }
    if (latest) onUpdated(latest.submission);
    setError(problems.join(' · ')); setBusy(false);
  }
  async function remove(file) {
    setBusy(true); setError('');
    try { onUpdated((await removeSubmissionFile(project.uuid, box.number, file.uuid)).submission); } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  const row = (file, own) => <li key={file.uuid}>
    <button type="button" className="xo-cp-attachment" onClick={() => openSubmissionFile(file.uuid).catch((caught) => setError(errorMessage(caught)))}>
      <FileText aria-hidden="true" /><span><strong>{file.name}</strong>
        <small>{fileSize(file.bytes)} · {shortDay(file.addedAt)}{file.addedBy ? ` · ${file.addedBy}` : ''}</small></span><Download aria-hidden="true" /></button>
    {own && <button type="button" className="xo-icon-btn" aria-label={t('file_remove_named', { name: file.name })} disabled={busy} onClick={() => remove(file)}>
      <X aria-hidden="true" /></button>}
  </li>;
  return <section className="xo-cp-card xo-cp-files">
    <h3><Paperclip aria-hidden="true" />{t('files_title')}</h3>
    {ours.length > 0 && <><small className="xo-cp-files-group">{t('files_from_lab')}</small><ul>{ours.map((file) => row(file, false))}</ul></>}
    <small className="xo-cp-files-group">{t('files_yours')}</small>
    {theirs.length === 0 ? <p className="xo-cp-card-note">{t('files_none_yours')}</p> : <ul>{theirs.map((file) => row(file, true))}</ul>}
    <input ref={input} type="file" multiple hidden accept={FILE_ACCEPT} onChange={(event) => { const list = [...(event.target.files || [])]; event.target.value = ''; add(list); }} />
    <button type="button" className="xo-btn-2" disabled={busy} onClick={() => input.current?.click()}><Upload aria-hidden="true" />{t(busy ? 'files_adding' : 'files_add')}</button>
    <p className="xo-cp-card-note">{t('files_hint')}</p>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </section>;
}

// One submission once sent: where the box goes (until it arrives), where each sample is (their name
// beside our code, and its patient reports once asked for), our QC results and their answers, and at
// its very end the patient reports; beside it its files, the project's price and invoices.
function BoxView({ me, project, box, columns, compare = [], languages, journey, justSent, fileProblems = [], onUpdated }) {
  const [error, setError] = useState('');
  const banner = useRef(null);
  // Submitted from the bottom of the form: up to what was said and what comes next.
  useEffect(() => { if (justSent) banner.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); }, [justSent]);
  const count = (status) => box.samples.filter((sample) => sample.status === status).length;
  const boxComing = box.receivedCount === 0;
  const reported = box.reports?.answer === 'wanted';
  // Their name, our code, where it is (and its patient report), then what they wrote, in groups.
  const lead = [
    { key: 'name', label: t('sample_name'), cell: (sample) => <strong>{sample.sampleName}</strong> },
    { key: 'code', label: t('our_code'), cell: (sample) => <code>{sample.code}</code> },
    { key: 'where', label: t('where'), cell: (sample) => <SampleStatus sample={sample} /> },
    ...(reported ? [{ key: 'report', label: t('patient_report'), cell: (sample) => <ReportCell sample={sample} onError={setError} /> }] : []),
  ];
  // One column: where the box is, what to do while it is on its way (with where it goes), our QC
  // results, patient reports, the samples as wide as the page, then the files. What the project costs
  // and its invoices are on the project's page.
  return <>
    {justSent && <div ref={banner} className="xo-cust-made" role="status"><CircleCheck aria-hidden="true" />
      <p><strong>{counted('submitted_banner', box.sentCount)}</strong>{me.pointOfContact ? t('submitted_banner_told', { name: me.pointOfContact.name }) : t('submitted_banner_plain')}</p></div>}
    {fileProblems.length > 0 && <p className="xo-alert t-bad" role="alert">{t('files_not_added', { problems: fileProblems.join(' · ') })}</p>}
    <section className="xo-cp-card xo-cp-journey-card">{journey}</section>
    {boxComing && <section className="xo-cp-next">
      <div className="xo-cp-next-text"><small>{t('next_label')}</small><h2>{t('next_title')}</h2><p>{t('next_detail_label', { label: boxLabel(project, box.number) })}</p>
        <button type="button" className="xo-btn" onClick={() => downloadList(project, box)}><Download aria-hidden="true" />{t('download_list')}</button></div>
      <ShipTo place={project.shipTo} title={t('ship_to')} />
    </section>}
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <QcCard project={project} box={box} columns={columns} compare={compare} onUpdated={onUpdated} />
    <PatientReports project={project} box={box} languages={languages} onUpdated={onUpdated} />
    <dl className="xo-cust-sums">
      {['waiting', 'received', 'in_progress', 'completed'].map((status) => <div key={status}><dt>{t(`summary.${status}`)}</dt><dd>{count(status)}</dd></div>)}
    </dl>
    <Surface title={t('samples_title', { count: box.sentCount })} detail={t('samples_list_detail')}>
      {box.note && <p className="xo-cp-their-note"><MessageSquareText aria-hidden="true" /><span><strong>{t('your_note')}</strong> {box.note}</span></p>}
      <SampleAnswersTable columns={columns} samples={box.samples} lead={lead} flat />
    </Surface>
    <FilesCard project={project} box={box} onUpdated={onUpdated} />
  </>;
}

// A project as one of their pages loads it, and loads it again on request.
function useProject(uuid) {
  const [data, setData] = useState(null), [error, setError] = useState('');
  const load = useCallback(() => { setError(''); loadSubmission(uuid).then(setData).catch((caught) => setError(errorMessage(caught))); }, [uuid]);
  useEffect(load, [load]);
  return { data, setData, error, load };
}

// One submission of a continuous project in its list: when and by whom it was sent, of which sample
// type, where it is, and whether patient reports wait for their answer or were asked for.
function BoxRow({ project, box }) {
  const [tone, state] = boxState(box);
  return <li><Link to={boxPath(project.uuid, box.number)} className={`xo-cust-sub xo-cp-sub is-${box.stage}`}>
    <span className="xo-cust-tile" aria-hidden="true"><Package /></span>
    <span className="xo-cust-cell">
      <strong className="xo-cust-ref">{t('box', { number: box.number })}</strong>
      <span>{counted('samples_count', box.sentCount)} · {box.sampleType.name}</span>
      <small>{t('submitted_on_by', { date: shortDay(box.sentAt), name: box.sentBy || '' })}</small>
    </span>
    <span className="xo-cust-sub-journey"><BoxJourney box={box} /></span>
    <span className="xo-cust-cell is-money"><Pill tone={tone}>{t(state)}</Pill>
      {box.qcWaiting > 0 && <Pill tone="warn">{t('qc_to_answer')}</Pill>}
      {box.reportsOffered && box.reports === null && <Pill tone="warn">{t('reports_to_answer')}</Pill>}
      {box.reports?.answer === 'wanted' && <small>{t('reports_asked_short')}</small>}</span>
    <ChevronRight className="xo-cust-open" aria-hidden="true" />
  </Link></li>;
}

// A continuous project: open for their samples (a button to send a submission) or closed by us, its
// submissions newest first, and beside them where the boxes go, its price and its invoices.
function ContinuousProject({ me, project }) {
  const [error, setError] = useState('');
  const boxes = [...project.submissions].reverse();
  return <>
    {project.stage === 'closed' && <p className="xo-alert t-info">{t('project_closed', { date: day(project.closedAt) })}</p>}
    <div className="xo-cp-col">
      {project.takesSamples && boxes.length === 0 && <section className="xo-cp-next">
        <div className="xo-cp-next-text"><small>{t('next_label')}</small><h2>{t('continuous_first_title')}</h2><p>{t('continuous_first_detail')}</p>
          <Link className="xo-btn" to={`${projectPath(project.uuid)}/send`}><Plus aria-hidden="true" />{t('send_samples')}</Link></div>
        <ShipTo place={project.shipTo} title={t('ship_to')} />
      </section>}
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <ProjectFacts project={project} shipTo={project.takesSamples && boxes.length > 0} />
      <Surface title={t('boxes_title', { count: boxes.length })} detail={t('boxes_detail')}>
        {boxes.length === 0 ? <p className="xo-cust-quiet">{t('no_boxes_yet')}</p>
          : <ol className="xo-cust-rows">{boxes.map((box) => <BoxRow key={box.number} project={project} box={box} />)}</ol>}
      </Surface>
      <CustomerSharedData project={project} />
      <InvoicesCard project={project} onError={setError} />
      <ContactCard me={me} phoneOnly />
    </div>
  </>;
}

// One project. A one-time project: the submission form while it waits for their samples, then its
// one submission. A continuous one: its submissions, and sending another. Withdrawn: a word.
function ProjectPage({ me, onChanged }) {
  const { uuid } = useParams();
  const { data, setData, error, load } = useProject(uuid);
  const [justSent, setJustSent] = useState(false), [failure, setFailure] = useState(''), [fileProblems, setFileProblems] = useState([]);
  if (error && data === null) return <Failed message={error} onRetry={load} />;
  if (data === null) return <Loading label={t('loading')} />;
  const { submission: project, columns } = data;
  const [tone, state] = stateOf(project);
  const title = t('project', { number: project.number });
  if (project.stage === 'withdrawn') {
    return <>
      <Head back={`${BASE}/projects`} title={title} detail={project.workflow.name} />
      <p className="xo-alert t-info">{t('project_withdrawn')}</p>
    </>;
  }
  const updated = (next) => { setData({ ...data, submission: next }); onChanged(); };
  if (project.kind === 'continuous') {
    return <>
      <Head back={`${BASE}/projects`} title={<>{title} <Pill tone={tone}>{t(state)}</Pill></>}
        detail={`${project.workflow.name} · ${t('kind.continuous')} · ${t('open_since', { date: day(project.requestedAt) })}`}>
        {project.takesSamples && <Link className="xo-btn" to={`${projectPath(project.uuid)}/send`}><Plus aria-hidden="true" />{t('send_samples')}</Link>}
      </Head>
      <ContinuousProject me={me} project={project} />
    </>;
  }
  const detail = project.sentAt === null
    ? `${project.workflow.name} · ${project.requestedBy ? t('asked_by', { name: project.requestedBy, date: day(project.requestedAt) }) : t('asked_on', { date: day(project.requestedAt) })}`
    : `${project.workflow.name} · ${project.sampleType?.name || ''} · ${t('submitted_on', { date: day(project.sentAt), name: project.sentBy || '' })}`;
  const box = project.submissions[0] ?? null;
  const head = <Head back={`${BASE}/projects`} title={<>{title} <Pill tone={tone}>{t(state)}</Pill></>} detail={detail}>
    {box && <button type="button" className="xo-btn-2" onClick={() => downloadList(project, box)}><Download aria-hidden="true" />{t('download_list')}</button>}
  </Head>;
  if (box === null) {
    return <>
      {head}
      <section className="xo-cp-card xo-cp-journey-card"><Journey submission={project} /></section>
      {failure && <p className="xo-alert t-bad" role="alert">{failure}</p>}
      <div className="xo-cp-col">
        <ProjectFacts project={project} />
        <SubmissionForm me={me} project={project} workflow={data.workflow}
          onSubmitted={(made, problems) => { setData({ ...data, submission: made.submission }); setJustSent(true); setFileProblems(problems); onChanged(); }} />
        <InvoicesCard project={project} onError={setFailure} />
        <ContactCard me={me} phoneOnly />
      </div>
    </>;
  }
  return <>
    {head}
    <BoxView me={me} project={project} box={box} columns={columns} compare={data.qcCompare ?? []} languages={data.languages}
      journey={<Journey submission={project} />} justSent={justSent} fileProblems={fileProblems} onUpdated={updated} />
    <CustomerSharedData project={project} />
  </>;
}

// A new submission of a continuous project: the submission form, then that submission's own page.
function SendPage({ me, onChanged }) {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { data, error, load } = useProject(uuid);
  if (error && data === null) return <Failed message={error} onRetry={load} />;
  if (data === null) return <Loading label={t('loading')} />;
  const project = data.submission;
  if (project.kind !== 'continuous' || !project.takesSamples) return <Navigate to={projectPath(uuid)} replace />;
  return <>
    <Head back={projectPath(uuid)} backLabel={t('project', { number: project.number })} title={t('send_title')}
      detail={`${t('project', { number: project.number })} · ${project.workflow.name} · ${t('box', { number: project.submissions.length + 1 })}`} />
    <SubmissionForm me={me} project={project} workflow={data.workflow}
      onSubmitted={(made, problems) => { onChanged(); navigate(boxPath(uuid, made.sent), { state: { justSent: true, fileProblems: problems } }); }} />
  </>;
}

// One submission of a continuous project, on its own page.
function BoxPage({ me, onChanged }) {
  const { uuid, number } = useParams();
  const location = useLocation();
  const { data, setData, error, load } = useProject(uuid);
  if (error && data === null) return <Failed message={error} onRetry={load} />;
  if (data === null) return <Loading label={t('loading')} />;
  const project = data.submission;
  const box = project.submissions.find((entry) => entry.number === Number(number));
  if (!box) return <Navigate to={projectPath(uuid)} replace />;
  const [tone, state] = boxState(box);
  return <>
    <Head back={projectPath(uuid)} backLabel={t('project', { number: project.number })} title={<>{t('box', { number: box.number })} <Pill tone={tone}>{t(state)}</Pill></>}
      detail={`${t('project', { number: project.number })} · ${project.workflow.name} · ${box.sampleType.name} · ${t('submitted_on', { date: day(box.sentAt), name: box.sentBy || '' })}`}>
      <button type="button" className="xo-btn-2" onClick={() => downloadList(project, box)}><Download aria-hidden="true" />{t('download_list')}</button>
    </Head>
    <BoxView me={me} project={project} box={box} columns={data.columns} compare={data.qcCompare ?? []} languages={data.languages} journey={<BoxJourney box={box} />}
      justSent={Boolean(location.state?.justSent)} fileProblems={location.state?.fileProblems ?? []}
      onUpdated={(next) => { setData({ ...data, submission: next }); onChanged(); }} />
  </>;
}

// An address from before projects (a submission, from its email): the same project.
function OldSubmission() {
  const { uuid } = useParams();
  return <Navigate to={projectPath(uuid)} replace />;
}

function ToSignIn() {
  const location = useLocation();
  const asked = location.pathname.replace(/\/$/, '');
  return <Navigate to={`${BASE}/sign-in${asked !== BASE ? `?next=${encodeURIComponent(asked)}` : ''}`} replace />;
}

function SignedIn() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null), [overview, setOverview] = useState(null), [error, setError] = useState(''), [pageError, setPageError] = useState('');
  const reload = useCallback(() => loadOverview().then(setOverview).catch((caught) => setError(caught.status === 401 ? 'signed-out' : errorMessage(caught))), []);
  useEffect(() => {
    loadMe().then(setMe).catch((caught) => setError(caught.status === 401 ? 'signed-out' : errorMessage(caught)));
    reload();
  }, [reload]);
  if (error === 'signed-out') return <ToSignIn />;
  if (error) return <main id="xo" className="xo-cp-root"><div className="xo-cp-alone"><Failed message={error} /></div></main>;
  if (me === null) return <main id="xo" className="xo-cp-root"><div className="xo-cp-alone"><Loading label={t('loading')} /></div></main>;
  const leave = () => signOut().finally(() => navigate(`${BASE}/sign-in`, { replace: true }));
  const page = (element) => (overview === null ? <Loading label={t('loading')} /> : element);
  return <Shell me={me} overview={overview} onSignOut={leave}>
    {pageError && <p className="xo-alert t-bad" role="alert">{pageError}</p>}
    <Routes>
      <Route index element={page(<Overview me={me} overview={overview} onError={setPageError} />)} />
      <Route path="projects" element={page(<Projects overview={overview} />)} />
      <Route path="projects/:uuid" element={<ProjectPage me={me} onChanged={reload} />} />
      <Route path="projects/:uuid/send" element={<SendPage me={me} onChanged={reload} />} />
      <Route path="projects/:uuid/submissions/:number" element={<BoxPage me={me} onChanged={reload} />} />
      <Route path="submissions" element={<Navigate to={`${BASE}/projects`} replace />} />
      <Route path="submissions/:uuid" element={<OldSubmission />} />
      <Route path="invoices" element={page(<Invoices overview={overview} onError={setPageError} />)} />
      <Route path="account" element={<Account me={me} onSignOut={leave} />} />
      <Route path="*" element={<Navigate to={BASE} replace />} />
    </Routes>
  </Shell>;
}

export default function CustomerPortal() {
  return <Routes>
    <Route path="join/:token" element={<Join />} />
    <Route path="sign-in" element={<SignIn />} />
    <Route path="forgot" element={<Forgot />} />
    <Route path="reset/:token" element={<Reset />} />
    <Route path="*" element={customerSession() ? <SignedIn /> : <ToSignIn />} />
  </Routes>;
}
